import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import {
    DataPlatformIndicatorFilter,
    getDataPlatformIndicators,
    getMultirateLowFilterValue,
} from 'custom_hooks/data_hooks/data_source/dataPlatformIndicatorDataSource';
import {
    getPrometheusIndicators,
    IndicatorFilter,
} from 'custom_hooks/data_hooks/data_source/indicatorDataSource';
import { infiniteDataSourceQueryOptions } from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import type { WorkbenchStateValues } from 'pages/workbench/state/types';
import { excludeNullAndUndefined } from 'utils/array';

// The indicator legend lists its indicators through these data sources. Build
// their filters and queries here so that other readers of the legend's
// indicators (e.g. picking default selections) share its cache entries.

export const PrometheusLegendDataSourceName = 'indicator-multi-select';
export const DataPlatformLegendDataSourceName = 'data-platform-indicator-multi-select';

export const PrometheusLegendSort: DataSourceSort[] = [
    { field: 'SystemSortOrder', dir: 'asc' },
    { field: 'Id', dir: 'asc' },
];
/**
 * How long the legend treats its indicators as fresh. Long enough that the
 * legend doesn't refetch a list another reader just loaded through the same
 * query; edits still refetch through invalidation.
 */
export const LegendStaleTime = 30_000;

/** Leaves ordering to the Data Platform's default. */
export const DataPlatformLegendSort: DataSourceSort[] = [];

/** The Workbench state the legend's indicator filters are built from. */
export type LegendFilterState = Pick<
    WorkbenchStateValues,
    | 'customIndicatorFilterIds'
    | 'indicatorTypes'
    | 'sensorRoleTypes'
    | 'orientations'
    | 'filtered'
    | 'collectionFilter'
    | 'dataTypes'
    | 'calculations'
    | 'bands'
    | 'labels'
    | 'showHistoricalIndicators'
>;

export function getPrometheusLegendFilter(
    groupIds: string[],
    state: LegendFilterState,
): IndicatorFilter {
    return {
        groupIds,
        customFilterIds: state.customIndicatorFilterIds,
        // Prometheus does not need to be able to handle the ultrasound quick filter, as that only exists
        // for timeseries cutover, so we can safely filter it out here.
        indicatorTypes: state.indicatorTypes.filter((type) => type != 'ultrasound'),
        // Prometheus does not need to be able to handle the filtering by null, as that only exists
        // for timeseries cutover, so we can safely filter out nulls here.
        sensorRoleTypes: excludeNullAndUndefined(state.sensorRoleTypes),
    };
}

export function getDataPlatformLegendFilter(
    groupIds: string[],
    state: LegendFilterState,
): DataPlatformIndicatorFilter {
    return {
        groupIds,
        customFilterIds: state.customIndicatorFilterIds,
        indicatorTypes: state.indicatorTypes,
        sensorRoleTypes: state.sensorRoleTypes,
        orientations: state.orientations,
        filtered: state.filtered,
        multirateLow: getMultirateLowFilterValue(state.collectionFilter),
        dataTypes: state.dataTypes,
        calculations: state.calculations,
        bands: state.bands,
        labels: state.labels,
        showHistoricalIndicators: state.showHistoricalIndicators,
    };
}

export function prometheusLegendQueryOptions(
    groupIds: string[],
    state: LegendFilterState,
) {
    return infiniteDataSourceQueryOptions({
        name: PrometheusLegendDataSourceName,
        filter: getPrometheusLegendFilter(groupIds, state),
        sort: PrometheusLegendSort,
        query: getPrometheusIndicators,
    });
}

export function dataPlatformLegendQueryOptions(
    groupIds: string[],
    state: LegendFilterState,
): ReturnType<
    typeof infiniteDataSourceQueryOptions<
        DataPlatformApi.IndicatorResponse,
        DataPlatformIndicatorFilter
    >
> {
    return infiniteDataSourceQueryOptions({
        name: DataPlatformLegendDataSourceName,
        filter: getDataPlatformLegendFilter(groupIds, state),
        sort: DataPlatformLegendSort,
        query: getDataPlatformIndicators,
    });
}
